import { useLayoutEffect, useRef, useState } from "react";

/**
 * Hook to track the height of a DOM element using ResizeObserver.
 * Returns a ref to attach to the element and the current height.
 *
 * @returns Object containing the ref and the current height in pixels (or null if not yet measured)
 *
 * @example
 * const { ref, height } = useElementHeight();
 * return <div ref={ref} style={{ height: `${height}px` }}>Content</div>
 */
export const useElementHeight = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const element = ref.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const nextHeight = Math.round(entry.contentRect.height);
      setHeight((prev) => (prev === nextHeight ? prev : nextHeight));
    });

    setHeight(Math.round(element.getBoundingClientRect().height));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, height };
};
